import { MenuItem } from '@szhsin/react-menu'
import { useGoogleLogin } from '@react-oauth/google'
import { useLocalStorage } from 'usehooks-ts'
import { useNotification } from '../../../providers/NotificationProvider'
import { errorIcon } from '../../../components/Notification'
import { ExternalCalendarList } from './ExternalCalendarList'
import {
  useGoogleAccountsQuery,
  useHasGoogleAccounts,
  useAddGoogleAccount,
  useRemoveGoogleAccount,
  useGoogleAccountDisplay,
  useGoogleCalendarsForAccount,
} from '../../../contexts/GoogleCalendar'
import type { GoogleAccount } from '../../../lib/google'
import { GoogleAccountErrorIndicator } from './GoogleAccountErrorIndicator'
import { useCalendarAuth } from '../../../contexts/CalendarAuth/CalendarAuthContext'
import { useEffect } from 'react'
import { UnifiedAccountItem } from './UnifiedAccountItem'

/**
 * GoogleCalendarSection Component
 *
 * Manages multiple Google Calendar accounts with the following features:
 *
 * 1. Active Indicator: The colored dot shows account status:
 *    - Green: Account is active and working properly
 *    - Red: Account has errors or is expired
 *
 * 2. Account Status: Accounts can have different statuses:
 *    - active: Account is working and tokens are valid
 *    - expired: OAuth tokens have expired and need refresh
 *    - error: There was an error accessing the account
 */
export function GoogleCalendarSection() {
  const { data: hasAccounts = false } = useHasGoogleAccounts()
  const { data: accounts = [] } = useGoogleAccountsQuery()
  const addAccountMutation = useAddGoogleAccount()
  const removeAccountMutation = useRemoveGoogleAccount()
  const { showNotification } = useNotification()
  const [expandedAccounts, setExpandedAccounts] = useLocalStorage<string[]>(
    'expandedGoogleAccounts',
    []
  )
  const { setGoogleAuthLoading } = useCalendarAuth()

  // Update loading state when mutation status changes
  useEffect(() => {
    setGoogleAuthLoading(addAccountMutation.isLoading)
  }, [addAccountMutation.isLoading, setGoogleAuthLoading])

  // Google OAuth login hook for adding new accounts
  const handleAddAccount = useGoogleLogin({
    flow: 'auth-code',
    redirect_uri: 'postmessage',
    ux_mode: 'popup',
    scope:
      'https://www.googleapis.com/auth/calendar https://www.googleapis.com/auth/calendar.events',
    onSuccess: ({ code }) => {
      addAccountMutation.mutate(code, {
        onSuccess: () => {
          showNotification({
            title: 'Google Calendar',
            message: hasAccounts
              ? 'Additional account added successfully'
              : 'Sign in successfully',
          })
        },
        onError: (error) => {
          const errorMessage =
            error instanceof Error ? error.message : 'Failed to add account'
          showNotification({
            title: 'Google Calendar',
            message: errorMessage,
            icon: errorIcon,
          })
        },
      })
    },
    onError: ({ error_description }) => {
      showNotification({
        title: 'Google Calendar',
        message: error_description ?? 'Failed to sign in',
        icon: errorIcon,
      })
    },
  })

  function handleRemoveAccount(accountId: string) {
    removeAccountMutation.mutate(accountId, {
      onSuccess: () => {
        showNotification({
          title: 'Google Calendar',
          message: 'Account removed successfully',
        })
      },
      onError: (error) => {
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to remove account'
        showNotification({
          title: 'Google Calendar',
          message: errorMessage,
          icon: errorIcon,
        })
      },
    })
  }

  const toggleAccountExpansion = (accountId: string) => {
    setExpandedAccounts((previous) => {
      return previous.includes(accountId)
        ? previous.filter((id) => id !== accountId)
        : [...previous, accountId]
    })
  }

  if (!hasAccounts) {
    // No accounts - show sign in option
    return (
      <MenuItem
        onClick={handleAddAccount}
        className='group transition-colors duration-150'
      >
        <div className='flex items-center gap-2'>
          <div className='flex h-5 w-5 items-center justify-center rounded bg-blue-500 text-white transition-transform duration-150 group-hover:scale-110'>
            <i className='fab fa-google text-xs' aria-hidden='true' />
          </div>
          <span className='text-sm'>Sign in with Google</span>
        </div>
      </MenuItem>
    )
  }

  // Show expanded view for all accounts
  return (
    <>
      <MenuItem disabled className='relative'>
        <div className='flex flex-col gap-2'>
          <div className='flex items-center justify-between'>
            <span className='text-xs font-bold text-gray-400 dark:text-gray-400'>
              GOOGLE ({accounts.length})
            </span>
            <button
              onClick={(e) => {
                e.stopPropagation()
                handleAddAccount()
              }}
              className='pr-0 text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300'
            >
              + Add Account
            </button>
          </div>
          <GoogleAccountErrorIndicator />
        </div>
      </MenuItem>

      {accounts.map((account: GoogleAccount) => (
        <GoogleAccountItem
          key={account.account_id}
          account={account}
          isExpanded={expandedAccounts.includes(account.account_id)}
          onToggleExpansion={() => {
            toggleAccountExpansion(account.account_id)
          }}
          onRemove={() => {
            handleRemoveAccount(account.account_id)
          }}
        />
      ))}
    </>
  )
}

type GoogleAccountItemProps = {
  account: GoogleAccount
  isExpanded: boolean
  onToggleExpansion: () => void
  onRemove: () => void
}

function GoogleAccountItem({
  account,
  isExpanded,
  onToggleExpansion,
  onRemove,
}: GoogleAccountItemProps) {
  const { data: accountDisplay } = useGoogleAccountDisplay(account.account_id)
  const { data: accountCalendars = [] } = useGoogleCalendarsForAccount(
    account.account_id
  )

  // Move early return logic after all hooks to avoid Rules of Hooks violations
  if (!accountDisplay) {
    // eslint-disable-next-line unicorn/no-null -- react requires null
    return null
  }

  return (
    <UnifiedAccountItem
      accountName={accountDisplay.displayName}
      accountEmail={accountDisplay.email}
      calendars={accountCalendars}
      onRemove={onRemove}
      provider='google'
      isExpanded={isExpanded}
      onToggleExpansion={onToggleExpansion}
      // TODO: Add profileImage when available from Google API
    />
  )
}
